/* eslint-disable react/jsx-no-constructed-context-values */
import withTranslation from 'next-translate/withTranslation';
import { withRouter } from 'next/router';
import PropTypes from 'prop-types';
import { Component, createContext } from 'react';
import { UserContext } from '@providers/UserProvider/UserProvider';
import { getPusherClient } from '@services/PusherClient';
import { getBidAcceptances } from 'api/BidAcceptanceApi';
import { getCompanyInvites } from 'api/CompanyUserInviteAcceptanceApi';
import { getBidRequestAutomated } from 'api/RequestApi';
import PusherService from 'services/PusherService';

export const GeneralContext = createContext({
  bidAcceptances: [],
  companyInvites: [],
  getAndSetBidAcceptances: () => {},
  getAndSetCompanyInvites: () => {},
  getApolloGQLClient: () => Object,
  isAutomated: false,
  pusher: {},
  pusherChannel: {
    bind: (_eventName, _callback) => {},
    unbind: (_eventName, _callback) => {},
  },
  setGeneralProperty: () => {}
});

// This provider should be used to store "general" or "modelish" app wide state requirements.
// UI/Layout related state should be stored in the LayoutProvider

class GeneralProvider extends Component {
  constructor (props) {
    super(props);

    this.state = {
      bidAcceptances: [],
      bidSubmissionChangeCount: 0,
      cloningIds: [],
      companyInvites: [],
      displayModalForUnawardedRequests: {},
      docParsingBidSubmissionFileUrl: '',
      hasStaleData: false,
      isAutomated: false,
      requestChangeCount: 0,
      unauthorizedRequestIds: []
    };

    this.pusher = getPusherClient();

    this.pusherChannelName = `user-${this.props.user.id}`;
    this.pusherChannel = this.pusher.subscribe(this.pusherChannelName);

    const trueUserId = this.props.user?.impersonating ? this.props.user.true_user?.id : null;

    this.trueUserChannelName = trueUserId ? `user-${trueUserId}` : null;
    this.trueUserChannel = trueUserId ? this.pusher.subscribe(this.trueUserChannelName) : null;

    this.setGeneralProperty = this.setGeneralProperty.bind(this);
    this.incrementRequestChangeCount = this.incrementRequestChangeCount.bind(this);
    this.getAndSetCompanyInvites = this.getAndSetCompanyInvites.bind(this);
    this.getAndSetBidAcceptances = this.getAndSetBidAcceptances.bind(this);
    this.getApolloGQLClient = this.getApolloGQLClient.bind(this);
    this.areCloneJobsComplete = this.areCloneJobsComplete.bind(this);
  }

  componentDidMount () {
    if (this.trueUserChannel) {
      this.trueUserChannel.bind('change', e => {
        const service = new PusherService(e, this);

        service.handleEvents();
      });
    }

    this.pusherChannel.bind('change', e => {
      const service = new PusherService(e, this);

      service.handleEvents();
    });

    if (this.props.user.is_deeplink && this.props.user.deeplink_contextable_id) {
      this.setBidRequestAutomated();
    }

  }

  // The socket outlives this provider (it closes on sign-out). Unsubscribing through the client,
  // not the channel, drops the channel and its handlers so a remount starts clean.
  componentWillUnmount () {
    this.pusher.unsubscribe(this.pusherChannelName);
    if (this.trueUserChannelName) this.pusher.unsubscribe(this.trueUserChannelName);
  }

  setGeneralProperty (property) {
    this.setState(property);
  }

  // General non-GetStream notifications list functions

  getAndSetCompanyInvites () {
    return getCompanyInvites(['pending', 'bounced'])
      .then(response => {
        if (!response.success) return;

        const invites = response.company_invites;

        this.setGeneralProperty({
          companyInvites: invites
        });
      });
  };

  getAndSetBidAcceptances () {
    return getBidAcceptances(['not_answered', 'bounced'])
      .then(response => {
        if (!response.success) return;

        const acceptances = response.bid_acceptances;

        this.setGeneralProperty({
          bidAcceptances: acceptances
        });

      });
  };

  getApolloGQLClient () {
    return this.props.gqlClient;
  }

  // The deeplink context may not be a bid request, so a failed lookup leaves isAutomated false
  // (Messages stays visible) instead of alerting.
  setBidRequestAutomated () {
    return getBidRequestAutomated(this.props.user.deeplink_contextable_id).then(response => {
      if (!response.success) return;

      this.setState({ isAutomated: !!response.automated });
    });
  }

  areCloneJobsComplete () {
    const cloningKeys = Object.keys(this.state).map(k => k.includes('cloning_'));
    const areCloneJobsRunning = cloningKeys.some(k => !!this.state[k]);

    // If at least one clone job is running return false. If none are running return true.
    return !areCloneJobsRunning;
  }

  incrementRequestChangeCount () {
    this.setState(prev => ({ requestChangeCount: prev.requestChangeCount + 1 }));
  }

  render () {
    return (
      <GeneralContext.Provider
        displayName="GeneralProvider"
        value={{
          ...this.state,
          areCloneJobsComplete: this.areCloneJobsComplete,
          getAndSetBidAcceptances: this.getAndSetBidAcceptances,
          getAndSetCompanyInvites: this.getAndSetCompanyInvites,
          getApolloGQLClient: this.getApolloGQLClient,
          incrementRequestChangeCount: this.incrementRequestChangeCount,
          pusher: this.pusher,
          pusherChannel: this.pusherChannel,
          setGeneralProperty: this.setGeneralProperty
        }}
      >
        {this.props.children}
      </GeneralContext.Provider>
    );
  }
}

GeneralProvider.propTypes = {
  children: PropTypes.node.isRequired,
  gqlClient: PropTypes.shape().isRequired,
  user: PropTypes.shape().isRequired,

};

const exportedComponent = props => <UserContext.Consumer>
  {user => <GeneralProvider {...props} user={user} />}
</UserContext.Consumer>;

export default withRouter(withTranslation(exportedComponent, 'common'));

/*

State Documentation

unauthorizedRequestIds: Use to filter against when removing request associations from a distant component
requestChangeCount: Used with Request & Request Tabs to refetch data after any updates have been made to a request

*/
